// packages import
import { FC, useState, useEffect, useContext } from 'react'
import { Button } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBackIos'
import { useNavigate, useLocation } from 'react-router-dom'

// component import
import GenerateBillsHeader from '../../../components/billing/generateBills/GenerateBillsHeader'
import BillableCasesTable from '../../../components/billing/generateBills/BillableCasesTable'
import { CardBox } from '../../../theme/styleComponents'
import { Alert } from '../../../components/common/Alert'

// others
import {
  BillingCycle,
  useFetchBillableCasesLazyQuery,
  BillableCasesByType,
  FetchBillableCasesInput
} from '../../../../generated'
import { GRAPHQL_QUERY_POLICY } from '../../../constants'
import { AuthContext } from '../../../context/AuthContext'

interface LocationState {
  type?: 'attorney' | 'client'
}

export const GenerateBills: FC = (): JSX.Element => {
  const [billableCases, setBillableCases] = useState<BillableCasesByType | null>(null)
  const [selectedCases, setSelectedCases] = useState<string[]>([])
  const [filters, setFilters] = useState<FetchBillableCasesInput>({
    billingCycle: BillingCycle.EndOfMonth,
    customDate: new Date().toISOString()
  })

  const navigate = useNavigate()
  const location = useLocation()
  const { isParalegal, currentUser } = useContext(AuthContext)

  const state: LocationState = location.state
  const type = state?.type || 'attorney'

  const [fetchBillableCases, { loading }] = useFetchBillableCasesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onCompleted: data => {
      if (data?.fetchBillableCases?.cases) {
        setBillableCases(data.fetchBillableCases.cases)
      }
    },
    onError: error => {
      Alert.error(error.message || 'Failed to fetch billable cases')
      setBillableCases(null)
    }
  })

  const handleSetFilters = (newFilters: Partial<FetchBillableCasesInput>) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }

  const handleBack = () => {
    navigate('/billing/pre-bills')
  }

  useEffect(() => {
    if (filters.attorneyId || filters.clientId) {
      if (filters.billingCycle === BillingCycle.CustomDate && !filters.customDate) {
        Alert.error('Please select a custom date')
        return
      }

      fetchBillableCases({
        variables: {
          fetchBillableCasesInput: {
            ...(isParalegal && { paralegalId: currentUser?.id }),
            attorneyId: filters.attorneyId,
            clientId: filters.clientId,
            billingCycle: filters.billingCycle,
            customDate: filters.customDate
          }
        }
      })
    }
  }, [filters, fetchBillableCases, isParalegal, currentUser])

  return (
    <>
      <Button
        startIcon={<ArrowBackIcon sx={{ width: '13px' }} />}
        onClick={handleBack}
        sx={{
          color: '#5B5675',
          fontWeight: 600,
          fontSize: '14px'
        }}
      >
        BACK
      </Button>

      <CardBox sx={{ mt: 3 }}>
        <GenerateBillsHeader
          filters={filters}
          type={type}
          selectedCases={selectedCases}
          billableCases={billableCases}
          setFilters={handleSetFilters}
        />

        <BillableCasesTable
          billableCases={billableCases}
          type={type}
          loading={loading}
          hasFilters={!!(filters.attorneyId || filters.clientId)}
          selectedCases={selectedCases}
          onSelectedCasesChange={setSelectedCases}
        />
      </CardBox>
    </>
  )
}

export default GenerateBills
